import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';

import { useGetRecipientVaspsQuery } from '@/api/v1';
import { UseFormWithErrorsReturn } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import { CryptoNetworkType, CryptoPaymentForm } from '@/schemas/cryptoPaymentMethod';
import { VaspResponse } from '@/types/recipient';

interface Props {
  readonly form: UseFormWithErrorsReturn<CryptoPaymentForm>;
}

const networkOptions = [
  { value: CryptoNetworkType.ETH, label: 'Ethereum (USDC)' },
  { value: CryptoNetworkType.TRON, label: 'Tron (USDT)' },
];

const OTHER_VASP_ID = '__OTHER__';

export const CryptoAccountDetailFields: React.FC<Props> = ({ form }: Props): React.ReactElement => {
  const { register, watch, setValue } = form;
  const tr = useTranslator();

  const { data: vasps = [], isLoading: vaspsLoading } = useGetRecipientVaspsQuery();

  const [isNonCustodialWallet, network, vaspId, vaspName] = watch([
    'isNonCustodialWallet',
    'network',
    'vaspId',
    'vaspName',
  ]);

  const optionsWithOther = React.useMemo(
    (): readonly VaspResponse[] => [...vasps, { id: OTHER_VASP_ID, name: tr('CRYPTO_VASP_OTHER') }],
    [vasps, tr],
  );

  const isOtherSelected = vaspId === OTHER_VASP_ID;

  const onNonCustodialChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      setValue('isNonCustodialWallet', checked);
      if (checked) {
        setValue('vaspId', '');
        setValue('vaspName', '');
      }
    },
    [setValue],
  );

  const onVaspChange = React.useCallback(
    (_event: React.SyntheticEvent, value: VaspResponse | null): void => {
      const selectedId = value?.id ?? '';
      setValue('vaspId', selectedId);
      if (selectedId !== OTHER_VASP_ID) {
        setValue('vaspName', '');
      }
    },
    [setValue],
  );

  const onVaspNameChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setValue('vaspName', event.target.value);
    },
    [setValue],
  );

  const selectedVasp = React.useMemo(
    (): VaspResponse | null =>
      optionsWithOther.find((v: VaspResponse): boolean => v.id === vaspId) ?? null,
    [optionsWithOther, vaspId],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography variant="body2" color="text.primary">
        {tr('CRYPTO_ACCOUNT_INFORMATION')}
      </Typography>
      <TextField
        value={network}
        select={true}
        {...register({ name: 'network', label: tr('CRYPTO_NETWORK') })}>
        {networkOptions.map(
          (option: { value: string; label: string }): React.ReactElement => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ),
        )}
      </TextField>
      <TextField {...register({ name: 'walletAddress', label: tr('CRYPTO_WALLET_ADDRESS') })} />
      <FormControlLabel
        control={<Checkbox checked={isNonCustodialWallet} onChange={onNonCustodialChange} />}
        label={tr('CRYPTO_NON_CUSTODIAL_WALLET')}
      />
      {!isNonCustodialWallet ? (
        <>
          <Autocomplete
            options={optionsWithOther}
            loading={vaspsLoading}
            value={selectedVasp}
            onChange={onVaspChange}
            getOptionLabel={(option: VaspResponse): string => option.name}
            isOptionEqualToValue={(option: VaspResponse, value: VaspResponse): boolean =>
              option.id === value.id
            }
            renderInput={(params: object): React.ReactElement => (
              <TextField
                {...params}
                label={tr('CRYPTO_VASP')}
                error={!!form.formState.errors.vaspId}
                helperText={form.formState.errors.vaspId?.message}
              />
            )}
          />
          {isOtherSelected ? (
            <TextField
              label={tr('CRYPTO_VASP_NAME')}
              value={vaspName}
              onChange={onVaspNameChange}
              error={!!form.formState.errors.vaspName}
              helperText={form.formState.errors.vaspName?.message}
            />
          ) : null}
        </>
      ) : null}
    </Box>
  );
};
